import memoize from 'lodash/memoize';

import {UnifiedMembershipType} from 'common/report/queries/app-api/workspaces-client-types';
import {ProjectMember, UnifiedBaseMember, WorkspaceObj} from 'common/report/queries/app-api/workspaces-client';

import {baseComparator} from './array';

export function getWorkspaceDetailsUrl(workspace: {id: number; projectId: number}): string {
  return `/settings/project/${workspace.projectId}#views/detail/${workspace.id}`;
}

export function sortWorkspaces(a: WorkspaceObj, b: WorkspaceObj) {
  if (a.projectId < b.projectId) {
    return -1;
  } else if (a.projectId > b.projectId) {
    return 1;
  }

  if (a.isGlobal) {
    return -1;
  } else if (b.isGlobal) {
    return 1;
  }

  if (a.isDefault) {
    return -1;
  } else if (b.isDefault) {
    return 1;
  }

  const aNormalizeName = a.name.toLocaleLowerCase();
  const bNormalizeName = b.name.toLocaleLowerCase();
  return aNormalizeName < bNormalizeName ? -1 : a === b ? 0 : 1;
}

export function canCreateWorkspace(projectPermissions: string[] | Set<string> = []) {
  const managePermission = `manage_workspaces`;
  if (Array.isArray(projectPermissions)) {
    return projectPermissions.includes(managePermission);
  }
  return projectPermissions.has(managePermission);
}

function _aggregatedMembers(
  workspace: WorkspaceObj,
  validUsers: Set<string>,
  validUsersMap?: Map<string, ProjectMember>,
) {
  const allMembers = new Map<
    string,
    {user: UnifiedBaseMember; hasIndividualGrant?: boolean; teams?: string[]; orgs?: string[]}
  >();

  workspace.unifiedMemberList.forEach((member) => {
    const normalizedEmail = member.email.toLocaleLowerCase();
    if (!validUsers.has(normalizedEmail)) {
      return;
    }

    if (!allMembers.has(normalizedEmail)) {
      allMembers.set(normalizedEmail, {
        user: {
          id: member.id,
          name: member.name,
          email: member.email,
          invited: member.invited,
          entityType: member.entityType,
          role: validUsersMap ? validUsersMap.get(normalizedEmail).role : null,
        },
        teams: null,
        orgs: null,
      });
    }

    const foundMember = allMembers.get(normalizedEmail);

    switch (member.membershipType) {
      case UnifiedMembershipType.Individual:
        foundMember.hasIndividualGrant = true;
        break;
      case UnifiedMembershipType.Organization:
        foundMember.orgs = foundMember.orgs
          ? [...foundMember.orgs, member.orgName].sort(baseComparator())
          : [member.orgName];
        break;
      case UnifiedMembershipType.Team:
        foundMember.teams = foundMember.teams
          ? [...foundMember.teams, member.teamName].sort(baseComparator())
          : [member.teamName];
        break;
    }
  });

  return Array.from(allMembers.values());
}

/**
 * This takes a unified member list and list of active project users and returns a combined array of all
 * grants for an active user into a single object per user for instance, if a user has an individual grant
 * and multiple team grants, a single object would be returned that has a teams array with both team names
 * and hasIndividualGrant = true
 */
export const aggregatedMembers = memoize(_aggregatedMembers);

export const aggregatedServiceAccountMembers = memoize(_aggregatedMembers);

export const workspacesTrackingPrefix = `[Workspaces]`;
export const enum CommonTrackEvents {
  /** Workspace successfully created */
  CreateWorkspace = `Create Workspace`,
  /** Workspace successfully deleted */
  DeleteWorkspace = `Delete Workspace`,
  /** Workspace API Failure */
  APIFailure = `API Failure`,
}

export const enum CommonTrackProperties {
  /** Total number of workspaces within the project */
  ProjectWorkspaces = `Project Workspace Count`,
  /** Length of the workspace name string */
  NameLength = `Name Length`,
  /** Length of the workspace description string */
  DescriptionLength = `Description Length`,
  /** Project ID the workspace is associated with, can't rely on the super prop project_id because workspaces can be created via the chrome header */
  ProjectId = `Workspace Project ID`,
  /** API Request Description */
  ApiDescription = `API Description`,
  /** Error message pulled from the error object */
  Message = `Message`,
  /** The app location that caused the event (Chrome Header, Project Settings, Gallery)*/
  UISource = `UI Source`,
  /** Workspace ID that triggered the event, this is generally handled by the workspace_id super prop automatically, but at project level pages (settings, gallery) it must be specified, it also provides flexibility long term to distinguish events that have both the super property and this one */
  Id = `Workspace ID`,
  /** Workspace is marked default */
  DefaultWorkspace = `Is Default`,
  /** Workspace is marked global */
  GlobalWorkspace = `Is Global`,
  /** Can current user update the workspace */
  CanUpdateBasic = `Can Update Basic`,
  /** Can current user update the restricted flag  */
  CanUpdateRestricted = `Can Update Restricted`,
  /** Can current user update the visibility flag */
  CanUpdateVisibility = `Can Update Visibility`,
  /** Workspace creation time */
  Created = `Created At`,
  /** Workspace deletion time */
  Deleted = `Deleted At`,
}

/** UI elements which can trigger the track event, format is intended to be parsable by custom properties if needed */
export const enum UISourceObject {
  ChromeHeaderCreateWorkspace = `Chrome Header:Create Workspace Button`,

  ProjectSettingsWorkspaceDetails = `Project Settings:Workspace Details`,
  ProjectSettingsDetailsDelete = `Project Settings:Workspace Details:Delete`,
  ProjectSettingsDetailsTable = `Project Settings:Workspace Details:Overview`,
  ProjectSettingsUpdateFilters = `Project Settings:Workspace Details:Filters:Update`,
  ProjectSettingsAddUsersModal = `Project Settings:Workspace Details:Add Users Modal`,
  ProjectSettingsAddServiceAccountsModal = `Project Settings:Workspace Details:Add Service Accounts Modal`,
  ProjectSettingsAddTeamsModal = `Project Settings:Workspace Details:Add Teams Modal`,
  ProjectSettingsUsersFilterActionBar = `Project Settings:Workspace Details:Users:Filter Action Bar`,
  ProjectSettingsServiceAccountsFilterActionBar = `Project Settings:Workspace Details:Service Accounts:Filter Action Bar`,
  ProjectSettingsTeamsFilterActionBar = `Project Settings:Workspace Details:Teams:Filter Action Bar`,
  ProjectSettingsWorkspacesTable = `Project Settings:Workspaces List`,
  ProjectSettingsCreateWorkspace = `Project Settings:Workspaces List:Create Workspace Button`,
  ProjectSettingsFilterActionBar = `Project Settings:Workspaces List:Filter Action Bar`,

  GalleryCreateWorkspace = `Gallery:Create Workspace Button`,
  GalleryWorkspaceCTA = `Gallery:Workspace CTA Button`,
}

export const enum ApiFailureDescription {
  Creation = `Create Workspace`,
  Deletion = `Delete Workspace`,
  Update = `Update Workspace`,
  AddUsers = `Add Users`,
  RemoveUsers = `Remove Users`,
  AddServiceAccounts = `Add Service Accounts`,
  RemoveServiceAccounts = `Remove Service Accounts`,
  AddTeams = `Add Teams`,
  RemoveTeams = `Remove Teams`,
  Join = `Join Workspace`,
  Leave = `Leave Workspace`,
  Default = `Set Default Workspace`,
  GetAll = `Get All Workspaces`,
  Get = `Get Workspace`,
}

type SharedTacking<EventName> = [EventName, {[key in CommonTrackProperties]?: any}];
export function workspaceCreateEvent(
  workspaceCount: number,
  {name, description, projectId},
  source: UISourceObject,
): SharedTacking<CommonTrackEvents.CreateWorkspace> {
  return [
    CommonTrackEvents.CreateWorkspace,
    {
      [CommonTrackProperties.ProjectWorkspaces]: workspaceCount,
      [CommonTrackProperties.NameLength]: name.length,
      [CommonTrackProperties.DescriptionLength]: description.length,
      [CommonTrackProperties.ProjectId]: projectId,
      [CommonTrackProperties.UISource]: source,
    },
  ];
}

export function workspaceApiFailure(
  description: ApiFailureDescription,
  errorMessage: string,
  {source, projectId, workspaceId}: {source: UISourceObject; projectId?: number; workspaceId?: number},
): SharedTacking<CommonTrackEvents.APIFailure> {
  const props = {
    [CommonTrackProperties.Message]: errorMessage,
    [CommonTrackProperties.ApiDescription]: description,
  };

  if (source) {
    props[CommonTrackProperties.UISource] = source;
  }

  if (projectId) {
    props[CommonTrackProperties.ProjectId] = projectId;
  }

  if (workspaceId) {
    props[CommonTrackProperties.Id] = workspaceId;
  }

  return [CommonTrackEvents.APIFailure, props];
}

export function workspaceCommonProps(workspace: WorkspaceObj): {[key in CommonTrackProperties]?: any} {
  return {
    [CommonTrackProperties.Id]: workspace.id,
    [CommonTrackProperties.DefaultWorkspace]: workspace.isDefault,
    [CommonTrackProperties.GlobalWorkspace]: workspace.isGlobal,
    [CommonTrackProperties.CanUpdateBasic]: workspace.canUpdateBasic,
    [CommonTrackProperties.CanUpdateRestricted]: workspace.canUpdateRestricted,
    [CommonTrackProperties.CanUpdateVisibility]: workspace.canUpdateVisibility,
  };
}
